import { styled, Typography } from '@mui/material';

import { AppTextInput, FieldRow } from 'components/FormElements';
import { useState } from 'react';
import { round } from 'lodash';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { getCurrencySymbol, numberToCurrencyString } from 'further-ui/utils';
import { NumericFormat } from 'react-number-format';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import { Column } from 'further-ui/components/Layout';

const Amount = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  color: theme.palette.text.secondary,
  textAlign: 'right',
}));

const Input = styled(AppTextInput)({
  '& input': {
    textAlign: 'right',
  },
});

type Props = {
  onClose: () => void;
  onSplit: (amount: number) => void;
  bankOperationAmountTcy: number;
  transactionUnreconciledAmountTcy: number;
  currency: CurrencyCode;
};

const SplitTransactionDialog: React.FC<Props> = ({
  onClose,
  onSplit,
  bankOperationAmountTcy,
  transactionUnreconciledAmountTcy,
  currency,
}) => {
  const [amountToSplit, setAmountToSplit] = useState(() =>
    bankOperationAmountTcy <= transactionUnreconciledAmountTcy
      ? bankOperationAmountTcy
      : 0,
  );

  const handleSplit = () => {
    onSplit(amountToSplit);
  };

  return (
    <ConfirmDialog
      open
      onClose={onClose}
      maxWidth="sm"
      title="Split transaction"
      content={
        <Column spacing="md">
          <Typography variant="h6">
            Split transactions if you need to record a part payment.
          </Typography>
          <Column spacing="sm">
            <FieldRow title="Balance" centerTitle>
              <Amount variant="body2">
                {numberToCurrencyString(transactionUnreconciledAmountTcy, {
                  currency,
                })}
              </Amount>
            </FieldRow>
            <FieldRow title="Part payment" centerTitle>
              <NumericFormat
                decimalScale={2}
                min={0}
                max={transactionUnreconciledAmountTcy}
                required
                placeholder="Enter amount"
                allowNegative={false}
                allowLeadingZeros={false}
                thousandSeparator={true}
                value={amountToSplit}
                customInput={Input}
                prefix={getCurrencySymbol(currency, { withSpace: true })}
                onValueChange={({ floatValue }) =>
                  setAmountToSplit(floatValue ?? 0)
                }
              />
            </FieldRow>
            <FieldRow title="Remaining amount" centerTitle>
              <Amount variant="body2">
                {numberToCurrencyString(
                  round(transactionUnreconciledAmountTcy - amountToSplit, 2),
                  {
                    currency,
                  },
                )}
              </Amount>
            </FieldRow>
          </Column>
        </Column>
      }
      actions={{
        confirm: {
          onClick: handleSplit,
          label: 'Split',
        },
        cancel: {
          onClick: onClose,
          label: 'Cancel',
        },
      }}
    />
  );
};

export default SplitTransactionDialog;
